import AppConfig from 'constants/AppConfig';
import moment from 'moment-timezone';
import { useState } from 'react';
import { NotificationManager } from 'react-notifications';
import { generatePassword as generateRandomPassword } from 'utils/generate-password';
import IntlMessages from 'utils/intl-messages';

interface FormValue {
  [key: string]: any;
}

interface FileValue {
  [key: string]: any;
}

interface FormResult {
  value: FormValue;
  file: FileValue;
  setValue: (value: FormValue) => void;
  setFile: (file: FileValue) => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeNumber: (value: any, name: string) => void;
  onCheckChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  generatePassword: () => void;
  toggleValue: (passedValue: string) => void;
  editFile: (inputName: string, resizedName: string, fileId: string) => void;
  onUploadFile: (data: any, inputName: string, resizedName: string, fileId: string) => void;
  clearFields: (initFields: FormValue) => void;
  onChangeEditor: (html: string, name: string) => void;
  onEdit: (e: any, id: string, props: any, customPath?: string) => void;
  onChangeTab: (e: any, newValue: any) => void;
  onChangeFilterDate: (date: any, name: string) => void;
  changeAutoComplete: (e: any, val: any, name: string) => void;
  onChangeDate: (date: any, name: string) => void;
  blockInvalidChar: (e: React.KeyboardEvent, array: string[]) => void;
  onChangeTime: (date: any, name: string) => void;
  changeAutoCompleteTextField: (e: React.ChangeEvent<HTMLInputElement>, name: string) => void;
}

export default (input: FormValue, filesInput: FileValue = {} as FileValue): FormResult => {
  const [value, setInfo] = useState<FormValue>(input);
  const [file, setFile] = useState<FileValue>(filesInput);

  const setValue = (value: FormValue): void => {
    setInfo(value);
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e) {
      setInfo({
        ...value,
        [e.target.name]: e.target.value,
      });
    }
  };

  const onChangeNumber = (value: any, name: string): void => {
    setInfo({
      ...value,
      [name]: value,
    });
  };

  const onCheckChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e) {
      setInfo({
        ...value,
        [e.target.name]: e.target.checked,
      });
    }
  };

  const clearFields = (initFields: FormValue): void => {
    setInfo({
      ...value,
      ...initFields,
    });
  };

  //password actions
  const generatePassword = (): void => {
    setInfo({ ...value, password: generateRandomPassword() });
  };

  const toggleValue = (passedValue: string): void => {
    setInfo({
      ...value,
      [passedValue]: !value[passedValue],
    });
  };

  //File actions
  const onUploadFile = (data: any, inputName: string, resizedName: string, fileId: string): void => {
    if (data) {
      setFile({
        ...file,
        [inputName]: data.url,
        [resizedName]: data.urlResized,
        [fileId]: data.fileId,
      });
    } else
      NotificationManager.error(
        <IntlMessages id={'global.errors.notUpload'} />,
        <IntlMessages id={`${AppConfig.errorTitle}`} />,
        AppConfig.notificationTimeout
      );
  };

  const editFile = (inputName: string, resizedName: string, fileId: string): void => {
    setFile({
      ...file,
      [inputName]: undefined,
      [resizedName]: undefined,
      [fileId]: undefined,
    });
  };

  const onChangeEditor = (html: string, name: string): void => {
    setInfo({
      ...value,
      [name]: html,
    });
  };

  const onChangeTab = (e: any, newValue: any): void => {
    if (e) {
      setInfo({
        ...value,
        activeTab: newValue,
      });
    }
  };

  const onEdit = (_e: any, id: string, props: any, customPath?: string): void => {
    props.history.push(`${customPath ? customPath : 'edit'}/${id}`);
  };

  const onChangeFilterDate = (date: any, name: string): void => {
    setInfo({
      ...value,
      [name]: {
        gte: moment(date)
          .utc(date.valueOf() + date.utcOffset() * 60000)
          .startOf('day')
          .toISOString(),
        lte: moment(date)
          .utc(date.valueOf() + date.utcOffset() * 60000)
          .endOf('day')
          .toISOString(),
      },
    });
  };

  const onChangeDate = (date: any, name: string): void => {
    setInfo({
      ...value,
      [name]: moment(date)
        .utc(date.valueOf() + date.utcOffset() * 60000)
        .startOf('day')
        .toISOString(),
    });
  };

  const onChangeTime = (date: any, name: string): void => {
    setInfo({ ...value, [name]: moment(date).toISOString() });
  };

  const changeAutoComplete = (_e: any, val: any, name: string): void => {
    setInfo({
      ...value,
      [name]: val,
    });
  };

  const changeAutoCompleteTextField = (e: React.ChangeEvent<HTMLInputElement>, name: string): void => {
    setInfo({
      ...value,
      [name]: e.target.value,
    });
  };

  const blockInvalidChar = (e: React.KeyboardEvent, array: string[]): void => {
    array.includes(e.key) && e.preventDefault();
  };

  return {
    value,
    file,
    setValue,
    setFile,
    onChange,
    onChangeNumber,
    onCheckChange,
    generatePassword,
    toggleValue,
    editFile,
    onUploadFile,
    clearFields,
    onChangeEditor,
    onEdit,
    onChangeTab,
    onChangeFilterDate,
    changeAutoComplete,
    onChangeDate,
    blockInvalidChar,
    onChangeTime,
    changeAutoCompleteTextField,
  };
};
